// Sticky add-to-cart bar (lm-). Brand replacement for Horizon's native
// <sticky-add-to-cart> (disabled via enable_sticky_add_to_cart:false in
// product.json) so we control the styling and — critically — the
// lm-bundle-tiers interaction.
//
// Bundle products: lm-bundle-tiers hides the native buy form (display:none) and
// adds via its own /cart/add flow. So the sticky button does NOT own cart logic;
// it PROXIES whichever add control is actually live at click time — the visible
// bundle "Add" button if present, else the native add-to-cart button. This
// routes through the correct flow without duplicating it.
//
// Degrades gracefully (62% IG WebView): no IntersectionObserver / no scroll
// anchor / no add button => the bar stays off-screen (ships translated down) and
// the in-page controls remain the only path. Nothing here throws into the page.

if (!(window.customElements && customElements.get('lm-sticky-atc'))) {
  class LmStickyAtc extends HTMLElement {
    connectedCallback() {
      this.bar = this.querySelector('[data-lm-satc-bar]');
      this.button = this.querySelector('[data-lm-satc-add]');
      if (!this.bar || !this.button) return;

      // State flags, initialised before any early return below so a variant:update
      // arriving on a bar with no scroll anchor can still no-op safely.
      this.stuck = false;
      this.keyboardOpen = false;
      this.drawerOpen = false;
      // Server-rendered truth for the variant the page loaded with. Absent
      // attribute => assume sellable (the CSS rule fails open the same way).
      this.available = this.dataset.variantAvailable !== 'false';

      // Repricing is independent of the scroll anchor, so subscribe before the
      // anchor bail-out. Scoped to the section, not document: product cards call
      // stopPropagation() on this event (assets/product-card.js:218), and every
      // core listener scopes the same way (assets/product-price.js:22,
      // assets/sticky-add-to-cart.js:77).
      this.section = this.closest('.shopify-section');
      if (this.section) {
        this._onVariantUpdate = this.onVariantUpdate.bind(this);
        this.section.addEventListener('variant:update', this._onVariantUpdate);
      }

      // Scroll anchor: the bundle widget when present (it replaces the native buy
      // box on bundle products), else the native buy-buttons block.
      this.anchor =
        document.querySelector('lm-bundle-tiers') || document.querySelector('.buy-buttons-block');
      if (!this.anchor) return;

      // IntersectionObserver / measurement target. .buy-buttons-block is an inline
      // <span> (its width:100% is ignored on an inline box) wrapping block-level
      // children — its getBoundingClientRect is unreliable across engines, which is
      // why the bar showed on desktop but never on mobile. Observe a guaranteed
      // block-level node instead: the inner .product-form-buttons <div> on standard
      // products, the (display:block) bundle widget otherwise. Falls back to the
      // anchor itself if neither is found.
      this.observeTarget = this.anchor.querySelector('.product-form-buttons') || this.anchor;

      this._onClick = this.onClick.bind(this);
      this.button.addEventListener('click', this._onClick);

      // Hide while a text field is focused so the on-screen keyboard doesn't
      // collide with a bottom-fixed bar (IG WebView jank).
      this._onFocusIn = this.onFocusIn.bind(this);
      this._onFocusOut = this.onFocusOut.bind(this);
      document.addEventListener('focusin', this._onFocusIn);
      document.addEventListener('focusout', this._onFocusOut);

      // Re-hide when a cart add fires (drawer opens). Belt-and-suspenders — the
      // click handler also hides directly.
      //
      // 'cart:update' is the ONLY event to bind here: Horizon maps CartAddEvent
      // onto it (CartAddEvent.eventName === ThemeEvents.cartUpdate, assets/events.js:115),
      // so the bundle flow's own dispatch (assets/lm-bundle-tiers.js:529) lands here
      // too. There was a second listener for a 'cart:add' event that Horizon never
      // fires anywhere — removed as P2-4, dead weight that implied a contract that
      // does not exist.
      this._onCartUpdate = this.hideForDrawer.bind(this);
      document.addEventListener('cart:update', this._onCartUpdate);

      if (!('IntersectionObserver' in window)) return; // no-IO: bar stays hidden

      this.io = new IntersectionObserver(this.onIntersect.bind(this), { threshold: 0 });
      this.io.observe(this.observeTarget);
    }

    disconnectedCallback() {
      if (this.io) this.io.disconnect();
      if (this.button && this._onClick) this.button.removeEventListener('click', this._onClick);
      if (this.section && this._onVariantUpdate) {
        this.section.removeEventListener('variant:update', this._onVariantUpdate);
      }
      document.removeEventListener('focusin', this._onFocusIn);
      document.removeEventListener('focusout', this._onFocusOut);
      document.removeEventListener('cart:update', this._onCartUpdate);
    }

    // Reprice and re-gate on variant change.
    //
    // The heavy lifting is already done server-side: on a PDP the variant picker
    // fetches "/products/<handle>?option_values=…" with no section_id, so
    // event.detail.data.html is a parsed Document of the WHOLE page rendered for
    // the new variant (assets/variant-picker.js:300-317). Our block lives in the
    // product-information section, so its correct markup — money formatting,
    // compare-at, currency setting, volume pricing and all — is already in there.
    // We just lift our own price subtree out of it. No second fetch, and no money
    // formatting reimplemented in JS.
    //
    // Swapping innerHTML rather than morph()/replaceWith(): this file is a CLASSIC
    // script (no importmap, deliberately, for IG WebView), so @theme/morph is not
    // available, and innerHTML sidesteps cross-document node adoption. Because only
    // the price subtree changes, this.bar / this.button stay valid — unlike core's
    // whole-bar morph, nothing needs re-caching afterwards.
    onVariantUpdate(event) {
      const detail = event && event.detail;
      const data = detail && detail.data;
      if (!data) return;

      // Never let another product's picker (quick-add modal, featured product)
      // repoint this bar.
      const ownId = this.dataset.productId;
      if (ownId && data.productId && String(data.productId) !== String(ownId)) return;

      const doc = data.html;
      const fresh =
        (doc &&
          (doc.querySelector('lm-sticky-atc[data-product-id="' + ownId + '"]') ||
            doc.querySelector('lm-sticky-atc'))) ||
        null;

      if (fresh) {
        const newPrice = fresh.querySelector('[data-lm-satc-price]');
        const curPrice = this.querySelector('[data-lm-satc-price]');
        if (newPrice && curPrice) curPrice.innerHTML = newPrice.innerHTML;
      }

      // Availability, using core's exact test (assets/product-form.js:640-682):
      // a null resource means the chosen option combo resolves to no variant.
      const variant = detail.resource;
      let available;
      if (variant == null) {
        available = false;
      } else if (typeof variant.available === 'boolean') {
        available = variant.available;
      } else if (fresh) {
        available = fresh.dataset.variantAvailable !== 'false';
      } else {
        available = this.available;
      }

      this.available = available;
      this.dataset.variantAvailable = available ? 'true' : 'false';
      if (variant && variant.id) this.dataset.currentVariantId = variant.id;

      this.render();
    }

    onIntersect(entries) {
      const entry = entries[0];
      if (!entry) return;
      // Show only once the buy box has scrolled ABOVE the viewport top — not while
      // it's still below the fold on initial load. Use the observer-provided
      // boundingClientRect (no separate read) and test top <= 0: when not
      // intersecting AND its top edge is above the viewport, the box is fully past;
      // below the fold the top is positive, so the bar stays hidden.
      this.stuck = !entry.isIntersecting && entry.boundingClientRect.top <= 0;
      this.render();
    }

    onFocusIn(e) {
      const t = e.target;
      if (!t) return;
      // Only hide for fields that summon the on-screen keyboard — the collision we
      // actually care about. A variant <select> or radio, a stepper button, etc.
      // don't summon one, so hiding for them just flickered the bar away on tap.
      const typing =
        t.tagName === 'TEXTAREA' ||
        t.isContentEditable ||
        (t.tagName === 'INPUT' &&
          /^(text|search|email|url|tel|number|password)$/i.test(t.type || 'text'));
      if (typing) {
        this.keyboardOpen = true;
        this.render();
      }
    }

    onFocusOut() {
      this.keyboardOpen = false;
      this.render();
    }

    hideForDrawer() {
      this.drawerOpen = true;
      this.render();
      // Let it return after the drawer is dismissed and the shopper scrolls.
      window.setTimeout(() => {
        this.drawerOpen = false;
      }, 1200);
    }

    render() {
      // `available` is the same gate the CSS applies via
      // lm-sticky-atc[data-variant-available='false']. Keeping it here too means the
      // bar never even animates in for a variant that cannot be sold — the CSS alone
      // would hide it, but the transform would still be running underneath.
      const show = this.stuck && !this.keyboardOpen && !this.drawerOpen && this.available;
      this.bar.setAttribute('data-stuck', show ? 'true' : 'false');
    }

    // Resolve the live add control at click time (bundle reveals its button via
    // JS after load, so this must be lazy). A disabled bundle button means the
    // bundle's availability gate closed, so fall through to core's form rather
    // than proxying to a control that cannot sell.
    activeAddButton() {
      const bundleBtn = document.querySelector('[data-lm-bundle-add]');
      if (bundleBtn && !bundleBtn.hidden && !bundleBtn.disabled && bundleBtn.offsetParent !== null) {
        return bundleBtn;
      }
      return (
        document.querySelector('[ref="addToCartButton"]') ||
        document.querySelector('.buy-buttons-block button[name="add"]')
      );
    }

    onClick() {
      const target = this.activeAddButton();
      // Clicking a disabled target is a silent no-op, and hideForDrawer() would
      // still slide the bar away as if something happened. Scroll instead, so a
      // sold-out variant lands the shopper on the buy box that explains why.
      if (target && !target.disabled) {
        target.click();
        this.hideForDrawer();
      } else if (this.anchor && this.anchor.scrollIntoView) {
        // Last resort: bring the shopper to the buy box.
        this.anchor.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }

  customElements.define('lm-sticky-atc', LmStickyAtc);
}
